import React, { useMemo } from "react";
import { Box, Stack, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

const STAGE_PALETTE = [
  "primary.main",
  "info.main",
  "warning.main",
  "success.main",
  "secondary.main",
  "error.main",
];

export function formatStageSec(sec) {
  const s = Math.max(0, Math.round(Number(sec || 0)));
  if (!s) return "0m";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d${h ? ` ${h}h` : ""}`;
  if (h > 0) return `${h}h${m ? ` ${m}m` : ""}`;
  return `${m || 1}m`;
}

function resolveColor(theme, token, index) {
  const path = String(token || "").split(".");
  let cur = theme.palette;
  for (const part of path) {
    if (cur == null) break;
    cur = cur[part];
  }
  if (typeof cur === "string") return cur;
  const fallback = STAGE_PALETTE[index % STAGE_PALETTE.length];
  const fb = fallback.split(".");
  let c = theme.palette;
  for (const part of fb) c = c?.[part];
  return typeof c === "string" ? c : theme.palette.primary.main;
}

/**
 * Barra discreta de tempo acumulado por etapa (coluna).
 * Idas/voltas já vêm somadas no array `stageTime` da API.
 * Colunas concluídas não devem vir no array (filtradas na API).
 */
export default function TaskStageTimeBar({
  stageTime = [],
  totalSec = null,
  sx = {},
  height = 4,
  variant = "compact",
}) {
  const theme = useTheme();
  const { t } = useTranslation("teamTasks");
  const detail = variant === "detail";

  const segments = useMemo(() => {
    const list = (Array.isArray(stageTime) ? stageTime : [])
      .map((row, index) => ({
        key: row.columnId != null ? `c-${row.columnId}` : `n-${row.name}-${index}`,
        name: String(row.name || "").trim() || "—",
        sec: Math.max(0, Number(row.sec) || 0),
        visits: Math.max(0, Number(row.visits) || 0),
        index,
      }))
      .filter((row) => row.sec > 0);
    const sum =
      totalSec != null && Number(totalSec) > 0
        ? Number(totalSec)
        : list.reduce((acc, row) => acc + row.sec, 0);
    return { list, sum };
  }, [stageTime, totalSec]);

  if (!segments.list.length || !segments.sum) return null;

  const bar = (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: detail ? Math.max(height, 8) : height,
        borderRadius: detail ? 999 : 0,
        overflow: "hidden",
        bgcolor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.35 : 0.45),
        ...(!detail ? sx : null),
      }}
      aria-label={t("stageTimeBarAria")}
      onClick={(e) => e.stopPropagation()}
    >
      {segments.list.map((row) => {
        const pct = Math.max(1.5, (row.sec / segments.sum) * 100);
        const color = resolveColor(theme, STAGE_PALETTE[row.index % STAGE_PALETTE.length], row.index);
        const title =
          row.visits > 1
            ? t("stageTimeSegmentVisits", {
                stage: row.name,
                time: formatStageSec(row.sec),
                visits: row.visits,
              })
            : t("stageTimeSegment", {
                stage: row.name,
                time: formatStageSec(row.sec),
              });
        return (
          <Tooltip key={row.key} title={title} enterDelay={200} arrow placement="top">
            <Box
              sx={{
                width: `${pct}%`,
                minWidth: 2,
                height: "100%",
                bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.85 : 0.78),
                transition: "filter 0.15s ease",
                "&:hover": { filter: "brightness(1.12)" },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );

  if (!detail) return bar;

  return (
    <Box
      sx={{
        mt: 1.5,
        mb: 0.5,
        px: 1.25,
        py: 1.1,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
        bgcolor: alpha(theme.palette.action.hover, theme.palette.mode === "dark" ? 0.06 : 0.04),
        ...sx,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1} sx={{ mb: 0.85 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.02 }}>
          {t("stageTimeDetailTitle")}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {formatStageSec(segments.sum)}
        </Typography>
      </Stack>
      {bar}
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 1 }}>
        {segments.list.map((row) => {
          const color = resolveColor(theme, STAGE_PALETTE[row.index % STAGE_PALETTE.length], row.index);
          return (
            <Tooltip
              key={row.key}
              title={
                row.visits > 1
                  ? t("stageTimeSegmentVisits", {
                      stage: row.name,
                      time: formatStageSec(row.sec),
                      visits: row.visits,
                    })
                  : t("stageTimeSegment", {
                      stage: row.name,
                      time: formatStageSec(row.sec),
                    })
              }
              enterDelay={250}
            >
              <Stack direction="row" alignItems="center" spacing={0.6} sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    bgcolor: color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ fontWeight: 600, maxWidth: 140 }}
                >
                  {row.name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontVariantNumeric: "tabular-nums", opacity: 0.9 }}
                >
                  {formatStageSec(row.sec)}
                </Typography>
              </Stack>
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
}
